'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { callParkingBoxApi } from '../lib/parkingBoxApi';
import { useAuth } from '../hooks/useAuth';

type CameraType = 'external' | 'internal' | 'unknown';

type CameraListItem = {
  id: string;
  name: string;
  type: CameraType;
  camUrl?: string;
  device?: string;
  description?: string;
  areaId?: string;
};

const extractCameraSnapshotData = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if ('_data_' in payload) {
    const data = (payload as Record<string, unknown>)['_data_'];
    if (typeof data === 'string' && data.trim().length > 0) {
      return data.trim();
    }
  }

  if ('data' in payload) {
    const data = (payload as Record<string, unknown>).data;
    if (typeof data === 'string' && data.trim().length > 0) {
      return data.trim();
    }
  }

  return null;
};

const normalizeCameraSnapshotSource = (raw: string): string => {
  const trimmed = raw.trim();
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }
  const sanitized = trimmed.replace(/\s+/g, '');
  return `data:image/jpeg;base64,${sanitized}`;
};

const normalizeCameraType = (value: unknown): CameraType => {
  if (typeof value !== 'string') {
    return 'unknown';
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'external') {
    return 'external';
  }
  if (normalized === 'internal') {
    return 'internal';
  }
  return 'unknown';
};

const normalizeCameraList = (payload: unknown): CameraListItem[] => {
  const listPayload = Array.isArray(payload)
    ? payload
    : ((payload as { data?: unknown; _data_?: unknown })?._data_ ?? (payload as { data?: unknown })?.data ?? null);

  if (!Array.isArray(listPayload)) {
    return [];
  }

  return listPayload.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const idCandidate = record.cam_id ?? record.id ?? record.camId;
    const nameCandidate = record.name ?? record.cam_name ?? record.label ?? record.device ?? record.description;
    const camTypeCandidate = record.cam_type ?? record.type ?? record.camera_type ?? record.camType;
    const camUrlCandidate = record.cam_url ?? record.url ?? record.stream_url ?? record.source;
    const deviceCandidate = record.device ?? record.cam_device ?? record.camName;
    const descriptionCandidate = record.description ?? record.label;
    const areaCandidate = record.area_id ?? record.parking_area_id ?? record.parkingId;

    const id = typeof idCandidate === 'string' || typeof idCandidate === 'number' ? String(idCandidate).trim() : '';
    if (!id.length) {
      return [];
    }
    const name = typeof nameCandidate === 'string' && nameCandidate.trim().length > 0 ? nameCandidate.trim() : `Kamera ${id}`;
    const cameraItem: CameraListItem = {
      id,
      name,
      type: normalizeCameraType(camTypeCandidate),
    };
    if (typeof camUrlCandidate === 'string' && camUrlCandidate.trim().length > 0) {
      cameraItem.camUrl = camUrlCandidate.trim();
    }
    if (typeof deviceCandidate === 'string' && deviceCandidate.trim().length > 0) {
      cameraItem.device = deviceCandidate.trim();
    }
    if (typeof descriptionCandidate === 'string' && descriptionCandidate.trim().length > 0) {
      cameraItem.description = descriptionCandidate.trim();
    }
    if (typeof areaCandidate === 'string' || typeof areaCandidate === 'number' || typeof areaCandidate === 'bigint') {
      const normalizedArea = String(areaCandidate).trim();
      if (normalizedArea.length > 0) {
        cameraItem.areaId = normalizedArea;
      }
    }
    return [cameraItem];
  });
};

export default function AdminCamerasPage() {
  const { token, isAdmin } = useAuth();
  const [cameras, setCameras] = useState<CameraListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDeleteCamera, setPendingDeleteCamera] = useState<CameraListItem | null>(null);
  const [previewCamera, setPreviewCamera] = useState<CameraListItem | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const fetchCameras = useCallback(async () => {
    if (!isAdmin) {
      setIsLoading(false);
      setError('Brak uprawnień do wyświetlania listy kamer.');
      setCameras([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await callParkingBoxApi(
        {
          action: 'get-cams',
        },
        { jwtToken: token ?? undefined, useStoredToken: true },
      );
      const normalized = normalizeCameraList(response.json);
      setCameras(normalized);
      setError(null);
    } catch (fetchError) {
      const message = fetchError instanceof Error && fetchError.message.length > 0 ? fetchError.message : 'Nie udało się pobrać listy kamer.';
      setError(message);
      setCameras([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, token]);

  useEffect(() => {
    void fetchCameras();
  }, [fetchCameras]);

  const handleDeleteExternal = useCallback(
    async (camId: string) => {
      if (!isAdmin) {
        return;
      }
      setDeletingId(camId);
      try {
        await callParkingBoxApi(
          {
            action: 'delete-external-cam',
            _data_: {
              cam_id: camId,
            },
          },
          { jwtToken: token ?? undefined, useStoredToken: true },
        );
        await fetchCameras();
      } catch (deleteError) {
        const message = deleteError instanceof Error && deleteError.message.length > 0 ? deleteError.message : 'Nie udało się usunąć kamery.';
        setError(message);
      } finally {
        setDeletingId((current) => (current === camId ? null : current));
        setPendingDeleteCamera((current) => (current?.id === camId ? null : current));
      }
    },
    [fetchCameras, isAdmin, token],
  );

  const requestDeleteCamera = useCallback((camera: CameraListItem) => {
    setPendingDeleteCamera(camera);
  }, []);

  const cancelDeleteCamera = useCallback(() => {
    setPendingDeleteCamera(null);
  }, []);

  const confirmDeleteCamera = useCallback(async () => {
    if (!pendingDeleteCamera) {
      return;
    }
    await handleDeleteExternal(pendingDeleteCamera.id);
  }, [handleDeleteExternal, pendingDeleteCamera]);

  const handlePreviewCamera = useCallback(
    async (camera: CameraListItem) => {
      if (!isAdmin) {
        return;
      }
      setPreviewCamera(camera);
      setPreviewSrc(null);
      setPreviewError(null);
      setIsPreviewLoading(true);
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-cam-snapshot',
            _data_: {
              cam_id: camera.id,
            },
          },
          { jwtToken: token ?? undefined, useStoredToken: true },
        );
        const snapshotData = extractCameraSnapshotData(response.json);
        if (!snapshotData) {
          throw new Error('Brak danych obrazu.');
        }
        const src = normalizeCameraSnapshotSource(snapshotData);
        setPreviewSrc(src);
      } catch (previewError) {
        const message = previewError instanceof Error && previewError.message.length > 0 ? previewError.message : 'Nie udało się pobrać podglądu kamery.';
        setPreviewError(message);
      } finally {
        setIsPreviewLoading(false);
      }
    },
    [isAdmin, token],
  );

  const closePreview = useCallback(() => {
    setPreviewCamera(null);
    setPreviewSrc(null);
    setPreviewError(null);
    setIsPreviewLoading(false);
  }, []);

  const externalCameras = useMemo(() => cameras.filter((camera) => camera.type === 'external'), [cameras]);
  const internalCameras = useMemo(() => cameras.filter((camera) => camera.type === 'internal'), [cameras]);
  const unknownCameras = useMemo(() => cameras.filter((camera) => camera.type === 'unknown'), [cameras]);

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900 transition-colors dark:bg-neutral-950 dark:text-slate-100">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] transition-colors dark:border-white/10 dark:bg-neutral-900/80">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-500">Brak uprawnień</p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-800 dark:text-slate-50">Podgląd kamer niedostępny</h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            To konto nie posiada uprawnień administracyjnych wymaganych do zarządzania kamerami. Skontaktuj się z administratorem lub wróć do dozwolonych sekcji
            panelu.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold uppercase tracking-[0.25em] text-slate-600 transition hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-600 dark:border-white/10 dark:text-slate-200 dark:hover:border-emerald-400 dark:hover:bg-emerald-400/10 dark:hover:text-emerald-300"
            >
              Wróć do panelu
            </Link>
            <Link
              to="/admin/vehicles/new"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold uppercase tracking-[0.25em] text-slate-600 transition hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-600 dark:border-white/10 dark:text-slate-200 dark:hover:border-emerald-400 dark:hover:bg-emerald-400/10 dark:hover:text-emerald-300"
            >
              Dodaj pojazd
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const renderTable = (title: string, items: CameraListItem[], options?: { deletable?: boolean; emptyHint?: string }) => (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-colors dark:border-white/10 dark:bg-neutral-900/80">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-100">{title}</h2>
        <span className="rounded-full border border-emerald-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-600 dark:border-emerald-500/40 dark:text-emerald-300">
          {items.length} {items.length === 1 ? 'kamera' : 'kamery'}
        </span>
      </div>

      <div className="mt-4 overflow-auto rounded-2xl border border-slate-200 dark:border-white/10">
        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-white/10">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.25em] text-slate-500 dark:bg-white/5 dark:text-slate-300/80">
            <tr>
              <th className="px-4 py-3 text-left">Nazwa</th>
              <th className="px-4 py-3 text-left">Cam ID</th>
              <th className="px-4 py-3 text-left">Typ</th>
              <th className="px-4 py-3 text-left">URL / Device</th>
              <th className="px-4 py-3 text-left">Opis</th>
              <th className="px-4 py-3 text-left">Strefa</th>
              {options?.deletable ? <th className="px-4 py-3 text-right">Akcje</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {items.length === 0 ? (
              <tr>
                <td colSpan={options?.deletable ? 7 : 6} className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-300/80">
                  {options?.emptyHint ?? 'Brak kamer do wyświetlenia.'}
                </td>
              </tr>
            ) : (
              items.map((camera) => (
                <tr key={camera.id} className="bg-white/40 dark:bg-white/5">
                  <td className="px-4 py-3 font-semibold text-slate-800 dark:text-slate-100">{camera.name}</td>
                  <td className="px-4 py-3 font-mono text-[13px] text-slate-600 dark:text-slate-300">{camera.id}</td>
                  <td className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-300">
                    {camera.type === 'external' ? 'Zewnetrzna' : camera.type === 'internal' ? 'Wewnetrzna' : 'Nieznana'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-200">
                    {camera.camUrl ? (
                      <span className="break-all text-emerald-600 dark:text-emerald-300">{camera.camUrl}</span>
                    ) : camera.device ? (
                      <span className="text-slate-700 dark:text-slate-200">{camera.device}</span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">Brak</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-200">
                    {camera.description?.length ? camera.description : <span className="text-slate-400 dark:text-slate-500">Brak</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-200">
                    {camera.areaId ?? <span className="text-slate-400 dark:text-slate-500">--</span>}
                  </td>
                  {options?.deletable ? (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => void handlePreviewCamera(camera)}
                          disabled={isPreviewLoading && previewCamera?.id === camera.id}
                          className="rounded-lg border border-sky-300 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-600 transition hover:bg-sky-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-sky-400/70 dark:text-sky-200 dark:hover:bg-sky-400/20"
                        >
                          {isPreviewLoading && previewCamera?.id === camera.id ? 'Ładowanie...' : 'Podglad'}
                        </button>
                        <button
                          type="button"
                          onClick={() => requestDeleteCamera(camera)}
                          disabled={deletingId === camera.id}
                          className="rounded-lg border border-rose-300 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-rose-500 transition hover:bg-rose-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-400/70 dark:text-rose-300 dark:hover:bg-rose-500/20"
                        >
                          {deletingId === camera.id ? 'Usuwanie...' : 'Usuń'}
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-10 text-slate-900 transition-colors dark:bg-neutral-950 dark:text-slate-100">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 transition hover:text-emerald-600 dark:text-slate-300/80 dark:hover:text-emerald-300"
          >
            {'<-'} Wróć do panelu
          </Link>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void fetchCameras()}
              disabled={isLoading}
              className="rounded-full border border-emerald-300 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-400/70 dark:text-emerald-200"
            >
              {isLoading ? 'Odświeżanie...' : 'Odśwież listę'}
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.2)] transition-colors dark:border-white/10 dark:bg-neutral-900/80">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80">Administracja</p>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-800 dark:text-white">Kamery Parking Box</h1>
            </div>
            {error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-600 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200">
                {error}
              </div>
            ) : null}
          </div>
        </div>

        {renderTable('Kamery zewnetrzne', externalCameras, {
          deletable: true,
          emptyHint: isLoading ? 'Trwa ładowanie kamer...' : 'Brak kamer zewnetrznych.',
        })}

        {renderTable('Kamery wewnetrzne', internalCameras, {
          deletable: false,
          emptyHint: isLoading ? 'Trwa ładowanie kamer...' : 'Brak kamer wewnetrznych.',
        })}

        {unknownCameras.length > 0
          ? renderTable('Kamery bez oznaczonego typu', unknownCameras, {
              deletable: false,
              emptyHint: 'Brak kamer z nieznanym typem.',
            })
          : null}

        {pendingDeleteCamera ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-8">
            <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 text-slate-900 shadow-xl transition-colors dark:border-white/10 dark:bg-neutral-900 dark:text-slate-100">
              <h2 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">Usunąć kamerę?</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                Czy na pewno chcesz usunąć kamerę <span className="font-semibold text-emerald-600 dark:text-emerald-300">{pendingDeleteCamera.name}</span>? Tej
                operacji nie można cofnąć.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={cancelDeleteCamera}
                  disabled={Boolean(deletingId)}
                  className="rounded-2xl border border-slate-300 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-200"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={() => void confirmDeleteCamera()}
                  disabled={Boolean(deletingId)}
                  className="rounded-2xl border border-rose-400 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-rose-500 transition hover:bg-rose-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deletingId === pendingDeleteCamera.id ? 'Usuwanie...' : 'Usuń'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {previewCamera ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-8">
            <div className="w-full max-w-5xl rounded-3xl border border-slate-200 bg-white p-6 text-slate-900 shadow-xl transition-colors dark:border-white/10 dark:bg-neutral-900 dark:text-slate-100">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80">Podglad kamery</p>
                  <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">{previewCamera.name}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-300/80">
                    Cam ID: <span className="font-mono text-[13px] text-slate-700 dark:text-slate-200">{previewCamera.id}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handlePreviewCamera(previewCamera)}
                    disabled={isPreviewLoading}
                    className="rounded-xl border border-emerald-300 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-400/70 dark:text-emerald-200 dark:hover:bg-emerald-400/20"
                  >
                    {isPreviewLoading ? 'Odswiezanie...' : 'Odswiez'}
                  </button>
                  <button
                    type="button"
                    onClick={closePreview}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white dark:border-white/15 dark:text-slate-200"
                  >
                    Zamknij
                  </button>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center dark:border-white/10 dark:bg-white/5">
                {isPreviewLoading ? (
                  <div className="flex h-[420px] items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" aria-label="loading" />
                      <p className="text-xs uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300/80">Ładowanie podglądu...</p>
                    </div>
                  </div>
                ) : previewError ? (
                  <div className="flex h-[420px] items-center justify-center">
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200">
                      {previewError}
                    </div>
                  </div>
                ) : previewSrc ? (
                  <div className="flex max-h-[75vh] items-center justify-center overflow-hidden rounded-xl bg-black">
                    <img src={previewSrc} alt={`Podgląd kamery ${previewCamera.name}`} className="max-h-[75vh] w-full object-contain" />
                  </div>
                ) : (
                  <div className="flex h-[420px] items-center justify-center text-sm text-slate-500 dark:text-slate-300/80">Brak podglądu.</div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}
